import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Trash2, CheckCheck, ChevronLeft, LayoutDashboard, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import ConvergenLogo from "../components/ConvergenMark";
import ContentEditor from "../components/ContentEditor";

export default function AdminDashboard() {
  const { logout, user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads"); // 'leads' | 'content'
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, by_sector: [], by_dimension: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([api.get("/leads"), api.get("/leads/stats")]);
      setLeads(l.data);
      setStats(s.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onLogout = async () => { await logout(); navigate("/admin/login", { replace: true }); };

  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar este lead?")) return;
    await api.delete(`/leads/${id}`);
    setLeads((p) => p.filter((x) => x.id !== id));
    load();
  };

  const onMark = async (id) => {
    await api.patch(`/leads/${id}`, { status: "contacted" });
    setLeads((p) => p.map((x) => (x.id === id ? { ...x, status: "contacted" } : x)));
    load();
  };

  return (
    <div className="min-h-screen bg-pearl" data-testid="page-admin-dashboard">
      {/* TOP BAR */}
      <header className="bg-white border-b border-rule sticky top-0 z-30">
        <div className="page-container h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="text-text-muted hover:text-cerulean flex items-center gap-1 text-xs font-mono tracking-widest">
              <ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> SITIO
            </Link>
            <div className="hidden md:block"><ConvergenLogo size={40} /></div>
          </div>
          <div className="flex items-center gap-5">
            <span className="text-xs text-text-muted hidden md:block">
              {t.admin.welcome}, <span className="text-ink">{user?.name}</span>
            </span>
            <Button data-testid="admin-logout-btn" onClick={onLogout} variant="ghost" className="rounded-none text-xs tracking-widest uppercase">
              <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t.admin.logout}
            </Button>
          </div>
        </div>
      </header>

      <div className="page-container py-10 md:py-14">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="eyebrow">CONTROL · {tab === "leads" ? "LEADS" : "CONTENIDO"}</p>
            <h1 className="display-heading text-3xl md:text-4xl mt-3">
              {tab === "leads" ? t.admin.dashboard : "Editor de Contenido"}
            </h1>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-rule mb-10 flex gap-1">
          <TabBtn active={tab === "leads"} onClick={() => setTab("leads")} icon={LayoutDashboard} testid="tab-leads">
            Leads
          </TabBtn>
          <TabBtn active={tab === "content"} onClick={() => setTab("content")} icon={Sparkles} testid="tab-content">
            Contenido Home
          </TabBtn>
        </div>

        {tab === "content" ? <ContentEditor /> : <LeadsPanel
          loading={loading}
          leads={leads}
          stats={stats}
          t={t}
          onDelete={onDelete}
          onMark={onMark}
        />}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, testid, children }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-5 py-3 text-[12px] tracking-[0.18em] uppercase font-medium transition-colors border-b-2 -mb-px flex items-center gap-2 ${
        active ? "border-cerulean text-ink" : "border-transparent text-text-muted hover:text-ink"
      }`}
    >
      <Icon className="w-4 h-4" strokeWidth={1.5} />
      {children}
    </button>
  );
}

function LeadsPanel({ loading, leads, stats, t, onDelete, onMark }) {
  return (
    <>
      {/* STATS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <StatCard label={t.admin.total} value={stats.total} testid="stat-total" />
        <StatCard label={t.admin.new} value={stats.new} accent testid="stat-new" />
        <StatCard label={t.admin.by_sector} value={stats.by_sector.length} testid="stat-sectors" />
        <StatCard label={t.admin.by_dim} value={stats.by_dimension.length} testid="stat-dims" />
      </div>

      {/* BREAKDOWNS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
        <Breakdown title={t.admin.by_sector} items={stats.by_sector.map((s) => ({ k: s.sector, v: s.count }))} />
        <Breakdown title={t.admin.by_dim} items={stats.by_dimension.map((s) => ({ k: s.dimension, v: s.count }))} />
      </div>

      {/* TABLE */}
      <div className="bg-white border border-rule overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-rule text-[10px] font-mono tracking-[0.22em] uppercase text-text-muted bg-pearl">
          <div className="col-span-2">{t.admin.table_name}</div>
          <div className="col-span-3">{t.admin.table_email}</div>
          <div className="col-span-2">{t.admin.table_sector}</div>
          <div className="col-span-2">{t.admin.table_dim}</div>
          <div className="col-span-1">{t.admin.table_date}</div>
          <div className="col-span-1">{t.admin.table_status}</div>
          <div className="col-span-1 text-right">{t.admin.table_actions}</div>
        </div>

        {loading ? (
          <div className="px-6 py-16 text-center text-sm text-text-muted">…</div>
        ) : leads.length === 0 ? (
          <div className="px-6 py-16 text-center text-sm text-text-muted" data-testid="leads-empty">{t.admin.empty}</div>
        ) : (
          leads.map((l) => (
            <div key={l.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-5 border-b border-rule last:border-b-0 text-sm" data-testid={`lead-${l.id}`}>
              <div className="md:col-span-2 font-medium text-ink">
                {l.name}
                {l.organization && <div className="text-xs text-text-muted mt-1">{l.organization}</div>}
              </div>
              <div className="md:col-span-3 text-text-muted break-all">{l.email}</div>
              <div className="md:col-span-2 text-ink">{l.sector}</div>
              <div className="md:col-span-2 text-ink">{l.dimension}</div>
              <div className="md:col-span-1 text-xs text-text-muted">{new Date(l.created_at).toLocaleDateString()}</div>
              <div className="md:col-span-1">
                <span className={`text-[10px] font-mono tracking-widest uppercase px-2 py-1 ${l.status === "new" ? "bg-cerulean/10 text-cerulean" : "bg-pearl text-text-muted"}`}>
                  {l.status}
                </span>
              </div>
              <div className="md:col-span-1 flex md:justify-end gap-2">
                {l.status === "new" && (
                  <button data-testid={`mark-${l.id}`} onClick={() => onMark(l.id)} className="text-cerulean hover:text-ink p-1" title={t.admin.mark_contacted}>
                    <CheckCheck className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                )}
                <button data-testid={`del-${l.id}`} onClick={() => onDelete(l.id)} className="text-red-500 hover:text-red-700 p-1" title={t.admin.delete}>
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}

function StatCard({ label, value, accent, testid }) {
  return (
    <div className={`p-6 border ${accent ? "border-cerulean bg-cerulean/5" : "border-rule bg-white"}`} data-testid={testid}>
      <div className="text-[10px] font-mono tracking-[0.22em] uppercase text-text-muted">{label}</div>
      <div className={`mt-3 display-heading text-4xl ${accent ? "text-cerulean" : "text-ink"}`}>{value}</div>
    </div>
  );
}

function Breakdown({ title, items }) {
  const max = Math.max(1, ...items.map((i) => i.v));
  return (
    <div className="bg-white border border-rule p-6">
      <div className="text-[10px] font-mono tracking-[0.22em] uppercase text-cerulean mb-5">{title}</div>
      {items.length === 0 ? (
        <div className="text-sm text-text-muted">—</div>
      ) : (
        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-4 text-sm">
              <div className="w-1/3 text-ink truncate">{it.k}</div>
              <div className="flex-1 h-1.5 bg-pearl relative overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-cerulean" style={{ width: `${(it.v / max) * 100}%` }} />
              </div>
              <div className="text-text-muted text-xs font-mono w-8 text-right">{it.v}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
